import { Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
import { Iterable } from 'monaco-editor/esm/vs/base/common/iterator.js';
import { Disposable, toDisposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { createDecorator } from 'monaco-editor/esm/vs/platform/instantiation/common/instantiation.js';
const IChatAgentService = ( createDecorator('chatAgentService'));
class ChatAgentService extends Disposable {
    constructor() {
        super(...arguments);
        this._agents = ( new Map());
        this._onDidChangeAgents = this._register(( new Emitter()));
        this.onDidChangeAgents = this._onDidChangeAgents.event;
    }
    static { this.AGENT_LEADER = '@'; }
    dispose() {
        super.dispose();
        this._agents.clear();
    }
    registerAgent(agent) {
        if (( this._agents.has(agent.id))) {
            throw new Error(`Already registered an agent with id ${agent.id}`);
        }
        this._agents.set(agent.id, { agent });
        this._onDidChangeAgents.fire();
        return toDisposable(() => {
            if (this._agents.delete(agent.id)) {
                this._onDidChangeAgents.fire();
            }
        });
    }
    updateAgent(id, updateMetadata) {
        const data = this._agents.get(id);
        if (!data) {
            throw new Error(`No agent with id ${id} registered`);
        }
        data.agent.metadata = { ...data.agent.metadata, ...updateMetadata };
        this._onDidChangeAgents.fire();
    }
    getDefaultAgent() {
        return Iterable.find(( this._agents.values()), a => !!a.agent.metadata.isDefault)?.agent;
    }
    getSecondaryAgent() {
        return Iterable.find(( this._agents.values()), a => !!a.agent.metadata.isSecondary)?.agent;
    }
    getAgents() {
        return Array.from(( this._agents.values()), v => v.agent);
    }
    hasAgent(id) {
        return ( this._agents.has(id));
    }
    getAgent(id) {
        const data = this._agents.get(id);
        return data?.agent;
    }
    async invokeAgent(id, request, progress, history, token) {
        const data = this._agents.get(id);
        if (!data) {
            throw new Error(`No agent with id ${id}`);
        }
        return await data.agent.invoke(request, progress, history, token);
    }
    async getFollowups(id, sessionId, token) {
        const data = this._agents.get(id);
        if (!data) {
            throw new Error(`No agent with id ${id}`);
        }
        if (!data.agent.provideFollowups) {
            return [];
        }
        return data.agent.provideFollowups(sessionId, token);
    }
}
export { ChatAgentService, IChatAgentService };
